import { inject, Injectable } from '@angular/core';
import {
  AddressWidgetPickerConfiguration,
  Locale,
} from '$shared/components/address-widget-picker/model/address-widget-picker-configuration';
import { environment } from '$env/environment';
import { AuthenticationService } from '$core/services/auth.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AddressWidgetService } from '$core/services/address-widget.service';
import { SessionService } from '$core/services/session.service';
import { AddressWidgetPickerConfigType } from '$shared/components/address-widget-picker/model/address-widget-picker-config-type';
import { WidgetContact } from '$shared/components/address-widget-picker/model/address-widget-picker-contact';
import { transformBelpostUserTypeToWidgetUserType } from '$shared/components/address-widget-picker/model/address-widget-user-type';
import { LanguageService } from './language.service';

@Injectable()
export class ContactPickerConfigurationService {
  private readonly languageService = inject(LanguageService);

  private readonly token$ = this.authenticationService.getAccessTokenReactive();
  private readonly defaultUserType$ = this.sessionService.session$.pipe(
    map((user) =>
      user ? transformBelpostUserTypeToWidgetUserType(user.type) : null
    )
  );
  private readonly isDisabled$ = new BehaviorSubject(false);
  private readonly widgetValue$ = new BehaviorSubject<WidgetContact | null>(
    null
  );
  private readonly widgetConfig$ =
    new BehaviorSubject<AddressWidgetPickerConfigType | null>(null);
  private readonly rules$ = this.widgetConfig$.pipe(
    switchMap((config) => {
      return this.addressWidgetService.getAddressRules(config).pipe(
        catchError((error) => {
          console.error('Error loading address rules:', error);
          return of({});
        })
      );
    })
  );

  public readonly configuration$: Observable<AddressWidgetPickerConfiguration> =
    combineLatest([
      this.defaultUserType$,
      this.token$,
      this.rules$,
      this.isDisabled$,
      this.widgetConfig$,
      this.widgetValue$,
      this.languageService.currentLang$,
    ]).pipe(
      map(
        ([
          defaultUserType,
          token,
          rules,
          isDisabled,
          config,
          widgetValue,
          lang,
        ]) => ({
          authorization: token,
          context: config.context || 'portal',
          style: 'portal',
          locale: this.mapLocale(lang),
          user: {
            person: {
              type: defaultUserType,
            },
          },
          validationRules: rules[config.addressPersonType],
          disabled: isDisabled,
          type: config.addressPersonType,
          value: widgetValue || null,
          apiUrl: `${environment.serverUrl}/v1`,
          title: config.title,
          description: config.description || null,
          defaultContact:
            config.selectDefaultContact !== undefined
              ? config.selectDefaultContact
              : true,
        })
      )
    );

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly addressWidgetService: AddressWidgetService,
    private readonly sessionService: SessionService
  ) {}

  private mapLocale(locale: string): Locale {
    switch (locale) {
      case 'en': {
        return 'en';
      }
      case 'by': {
        return 'be';
      }
      default: {
        return 'ru';
      }
    }
  }

  public setWidgetConfig(config: AddressWidgetPickerConfigType): void {
    if (!config.addressPersonType) {
      throw new Error('addressPersonType is required');
    }
    if (!config.belpostServiceType) {
      throw new Error('rulesServiceType is required');
    }
    this.widgetConfig$.next(config);
  }

  public setIsDisabled(value: boolean) {
    this.isDisabled$.next(value);
  }

  public setWidgetValue(contact: WidgetContact | null): void {
    this.widgetValue$.next(contact);
  }
}
